/**
 * Servicio de Error Reporting
 *
 * Captura y reporta errores de la aplicación.
 * Por ahora guarda en Firestore, pero puede expandirse a Sentry.
 */

import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";

// Tipo de error reportado
export interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  timestamp: ReturnType<typeof serverTimestamp>;
  metadata?: Record<string, unknown>;
  severity: "error" | "warning" | "info";
  source:
    | "react-error-boundary"
    | "unhandled-rejection"
    | "global-error"
    | "manual";
}

const COLLECTION = "errorReports";

// Flag para evitar reportar en desarrollo (configurable)
const REPORT_IN_DEV = false;

/**
 * Reporta un error al servicio de tracking
 */
export async function reportError(
  error: Error,
  options: {
    componentStack?: string;
    metadata?: Record<string, unknown>;
    source?: ErrorReport["source"];
    severity?: ErrorReport["severity"];
  } = {},
): Promise<void> {
  // No reportar en desarrollo a menos que esté habilitado
  if (import.meta.env.DEV && !REPORT_IN_DEV) {
    console.log(
      "[ErrorReporting] Skipping report in development:",
      error.message,
    );
    return;
  }

  try {
    const errorReport: Omit<ErrorReport, "timestamp"> & {
      timestamp: ReturnType<typeof serverTimestamp>;
    } = {
      message: error.message,
      stack: error.stack,
      componentStack: options.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: auth.currentUser?.uid,
      timestamp: serverTimestamp(),
      metadata: options.metadata,
      severity: options.severity || "error",
      source: options.source || "manual",
    };

    await addDoc(collection(db, COLLECTION), errorReport);

    if (import.meta.env.DEV) {
      console.log("[ErrorReporting] Error reported successfully");
    }
  } catch (reportingError) {
    // No queremos que un error en el reporting rompa la app
    console.error("[ErrorReporting] Failed to report error:", reportingError);
  }
}

/**
 * Reporta un error desde React Error Boundary
 */
export function reportReactError(
  error: Error,
  componentStack?: string,
): Promise<void> {
  return reportError(error, {
    componentStack,
    source: "react-error-boundary",
    severity: "error",
  });
}

/**
 * Reporta un unhandled rejection
 */
export function reportUnhandledRejection(reason: unknown): Promise<void> {
  const error = reason instanceof Error ? reason : new Error(String(reason));

  return reportError(error, {
    source: "unhandled-rejection",
    severity: "error",
    metadata: { originalReason: String(reason) },
  });
}

/**
 * Reporta un error global (window.onerror)
 */
export function reportGlobalError(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
): Promise<void> {
  const error = new Error(message);

  return reportError(error, {
    source: "global-error",
    severity: "error",
    metadata: { source, lineno, colno },
  });
}

/**
 * Configura listeners globales para errores no capturados
 */
export function setupGlobalErrorHandlers(): void {
  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledRejection(event.reason);
  });

  // Global errors
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      reportError(error, {
        source: "global-error",
        metadata: { source, lineno, colno },
      });
    } else {
      reportGlobalError(String(message), source, lineno, colno);
    }
    return false; // No prevenir el log en consola
  };

  if (import.meta.env.DEV) {
    console.log("[ErrorReporting] Global error handlers configured");
  }
}
